import { ThemeEvents } from '@theme/events';

/**
 * Back in stock — collects an email against a sold-out variant.
 *
 * Two delivery targets:
 *  - klaviyo: the public client-side back-in-stock endpoint, using the same public
 *    company id the storefront already exposes (auto-detected when not configured)
 *  - endpoint: any URL that accepts a JSON POST, for a custom or third-party flow
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';
const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;

/** Reads the public Klaviyo company id off the storefront's own script tag. */
function detectKlaviyoCompanyId() {
  for (const script of document.scripts) {
    const match = script.src?.match(/klaviyo\.com\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js|company_id=([A-Za-z0-9]+)/);
    if (match) return match[1] || match[2];
  }

  return null;
}

/**
 * @param {object} options
 * @param {string} options.email
 * @param {string} options.variantId
 * @param {string} [options.provider] - 'klaviyo' (default) or 'endpoint'
 * @param {string} [options.companyId] - overrides the detected Klaviyo public id
 * @param {string} [options.endpoint] - required when provider is 'endpoint'
 * @param {Record<string, unknown>} [options.context] - extra fields for the custom endpoint
 * @returns {Promise<void>} resolves when the subscription was accepted
 */
export async function subscribeBackInStock({ email, variantId, provider = 'klaviyo', companyId, endpoint, context }) {
  if (!EMAIL_PATTERN.test(email)) throw new Error('invalid-email');
  if (!variantId) throw new Error('missing-variant');

  if (provider === 'endpoint') {
    if (!endpoint) throw new Error('missing-endpoint');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, variant_id: variantId, ...context }),
    });

    if (!response.ok) throw new Error('request-failed');
    return;
  }

  const company = companyId || detectKlaviyoCompanyId();
  if (!company) throw new Error('missing-company-id');

  const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(company)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    }),
  });

  // The endpoint answers 202 with an empty body on success.
  if (!response.ok) throw new Error('request-failed');
}

/**
 * Product-page block: watches the selected variant and offers the form while it is sold out.
 */
class NotifyMeComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-notify-email]');
    this.status = this.querySelector('[data-notify-status]');
    this.variantInput = this.querySelector('[data-notify-variant]');

    this.variants = new Map();
    for (const entry of (this.dataset.variants || '').split('|')) {
      const [id, available] = entry.split(':');
      if (id) this.variants.set(id, available === 'true');
    }

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.form?.addEventListener('submit', this.#onSubmit, { signal });

    this.#render(this.variantInput?.value ?? '');
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ resource?: { id?: string | number, available?: boolean }, data?: { productId?: string } }} */ (
      /** @type {any} */ (event).detail
    );

    if (detail?.data?.productId && this.dataset.productId && detail.data.productId !== this.dataset.productId) return;

    const id = detail?.resource?.id;
    if (id == null) return;

    // The event carries availability for the freshly selected variant; fall back to
    // the map rendered with the block for anything it does not report.
    if (typeof detail?.resource?.available === 'boolean') {
      this.variants.set(String(id), detail.resource.available);
    }

    this.#render(String(id));
  };

  /** @param {string} variantId */
  #render(variantId) {
    const available = this.variants.get(variantId);

    if (this.variantInput instanceof HTMLInputElement) this.variantInput.value = variantId;
    this.toggleAttribute('hidden', available !== false);

    if (this.dataset.lastVariant !== variantId) {
      this.dataset.lastVariant = variantId;
      this.removeAttribute('data-notify-done');
      if (this.status) this.status.textContent = '';
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const email = this.input instanceof HTMLInputElement ? this.input.value.trim() : '';
    const variantId = this.variantInput instanceof HTMLInputElement ? this.variantInput.value : '';
    const button = this.querySelector('button[type="submit"]');

    this.setAttribute('data-notify-loading', 'true');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      await subscribeBackInStock({
        email,
        variantId,
        provider: this.dataset.provider,
        companyId: this.dataset.companyId,
        endpoint: this.dataset.endpoint,
        context: { product_id: this.dataset.productId, product_title: this.dataset.productTitle },
      });

      this.setAttribute('data-notify-done', 'true');
      if (this.status) this.status.textContent = this.dataset.successText || '';
      if (this.input instanceof HTMLInputElement) this.input.value = '';
    } catch (reason) {
      const invalid = reason instanceof Error && reason.message === 'invalid-email';
      if (this.status) this.status.textContent = (invalid ? this.dataset.invalidText : this.dataset.errorText) || '';
      this.setAttribute('data-notify-error', 'true');
    } finally {
      this.removeAttribute('data-notify-loading');
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };
}

if (!customElements.get('notify-me-component')) {
  customElements.define('notify-me-component', NotifyMeComponent);
}

export { NotifyMeComponent, detectKlaviyoCompanyId };
